import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { Offer, OfferGroup, OfferInterface, ShippingInfos } from '../models/offer';
import { OfferVariant } from '../models/offer-definition';
import { Paginated } from '../models/papginated';
import { User } from '../models/user';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { ContextService } from './context.service';

export interface UserOffersParams {
    page: number;
    perPage: number;
    archived?: boolean | null;
    published?: boolean | null;
}

@Injectable({
    providedIn: 'root',
})
export class OfferService {
    constructor(
        private readonly apiService: ApiService,
        private readonly authService: AuthService,
        private readonly contextService: ContextService,
    ) {}

    create(data: OfferInterface): Observable<Offer> {
        return this.apiService.post('/api/offers', data).pipe(map((res) => new Offer(res)));
    }

    getUserOffers(user: User, params: UserOffersParams): Observable<Paginated<Offer>> {
        const query: Record<string, string | number | boolean> = {
            page: params.page,
            perPage: params.perPage,
        };
        if (params.archived !== undefined && params.archived !== null) {
            query['archived'] = params.archived ? 1 : 0;
        }
        if (params.published !== undefined && params.published !== null) {
            query['published'] = params.published ? 1 : 0;
        }
        const qs = ApiService.objectToQueryString(query);
        return this.apiService.get<{ items: Offer[]; total: number; page: number; perPage: number }>('/api/users/' + user.id + '/offers?' + qs).pipe(
            map((res) => ({
                items: (res.items ?? []).map((offer) => new Offer(offer)),
                total: res.total,
                page: res.page,
                perPage: res.perPage,
            })),
        );
    }

    get(id: number) {
        return this.apiService.get('/api/offers/' + id).pipe(map((data) => new Offer(data)));
    }

    getContactInfos(id: number) {
        return this.apiService.post<{ phone: string; email: string }>('/api/offers/' + id + '/contact', {}, true);
    }

    getPromoted() {
        return this.apiService
            .get<Offer[]>('/api/offers/promoted?platform=' + this.contextService.getPlatform())
            .pipe(map((res) => res.map((data) => new Offer(data))));
    }

    getDiscoverFeed(options: { exclude?: number[]; limit?: number } = {}): Observable<Offer[]> {
        const limit = options.limit ?? 8;
        const exclude = options.exclude ?? [];
        const params = new URLSearchParams();
        params.set('limit', String(limit));
        for (const id of exclude) {
            params.append('exclude[]', String(id));
        }
        const qs = params.toString();
        return this.apiService
            .get<{ items: Offer[] }>(`/api/offers/discover${qs ? '?' + qs : ''}`, false)
            .pipe(map((res) => (res.items ?? []).map((item) => new Offer(item))));
    }

    removeOffer(offer: Offer) {
        return this.apiService.delete('/api/offers/' + offer.id);
    }

    createGroup() {
        return this.apiService.post('/api/offer-groups', {}).pipe(map((res) => new OfferGroup(res)));
    }

    getGroup(groupId: number) {
        return this.apiService.get<Offer[]>('/api/offer-groups/' + groupId + '/offers').pipe(map((res) => res.map((o) => new Offer(o))));
    }

    getVariantName(data: any): Observable<{ id: number; name: string; value: string }[]> {
        return this.apiService.post<{ id: number; name: string; value: string }[]>('/api/offer-variants/name', data);
    }

    updateOffer(id: number, newValues: Record<string, unknown>) {
        return this.apiService.put('/api/offers/' + id, newValues).pipe(map((res) => new Offer(res)));
    }

    resubmitOffer(id: number) {
        return this.apiService.post('/api/offers/' + id + '/resubmit', {}).pipe(map((res) => new Offer(res)));
    }

    setPublished(id: number, published: boolean, eids?: { tasinmazId?: string; plakaNo?: string }): Observable<Offer> {
        return this.apiService.put('/api/offers/' + id + '/publish', { published, ...eids }).pipe(map((res) => new Offer(res)));
    }

    archiveOffer(id: number): Observable<Offer> {
        return this.apiService.post('/api/offers/' + id + '/archive', {}).pipe(map((res) => new Offer(res)));
    }

    unarchiveOffer(id: number): Observable<Offer> {
        return this.apiService.post('/api/offers/' + id + '/unarchive', {}).pipe(map((res) => new Offer(res)));
    }

    reactivateOffer(id: number, eids?: { tasinmazId?: string; plakaNo?: string }): Observable<Offer> {
        return this.apiService.post('/api/offers/' + id + '/reactivate', eids ?? {}).pipe(map((res) => new Offer(res)));
    }

    getShippingEstimations(offer: Offer, offerVariant: OfferVariant = null, currency: string): Observable<ShippingInfos> {
        if (!offerVariant) {
            return this.apiService.get<ShippingInfos>('/api/offers/' + offer.id + '/shipping-estimations?currency=' + currency);
        } else {
            return this.apiService.get<ShippingInfos>(
                '/api/offer/' + offer.id + '/variants/' + offerVariant.id + '/shipping-estimations?currency=' + currency,
            );
        }
    }

    checkSkuAvailability(sku: string[]): Observable<{ available: boolean; usedSku: string[] }> {
        return this.apiService.post('/api/offers/check-sku', { sku });
    }

    /**
     * Checks if a SKU is already used by another offer
     *
     * @param sku The SKU to check
     * @param excludeOfferId Optional ID of an offer to exclude from the check (for updates)
     * @returns Observable that emits true if the SKU exists, false otherwise
     */
    checkSkuExists(sku: string, excludeOfferId?: string): Observable<boolean> {
        return this.apiService
            .post<{ exists: boolean }>('/api/offers/check-sku-exists', {
                sku,
                excludeOfferId: excludeOfferId || null,
            })
            .pipe(map((response) => response.exists));
    }
}
